import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { UsersThree, Plus, EnvelopeSimple, Trash, PencilSimple, Power, Stethoscope, MapPin } from '@phosphor-icons/react';
import { toast } from 'sonner';

const DAY_LABELS = [
  ['mon', 'Lun'], ['tue', 'Mar'], ['wed', 'Mer'], ['thu', 'Gio'],
  ['fri', 'Ven'], ['sat', 'Sab'], ['sun', 'Dom'],
];

const EMPTY = {
  email: '', password: '', name: '', profession_code: '', specializations: '',
  bio: '', hourly_rate: '', phone: '', region: '', province: '', comune: '',
};

const CenterStaffPage = () => {
  const [center, setCenter] = useState(null);
  const [staff, setStaff] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, i] = await Promise.all([
        api.get('/api/center/staff'),
        api.get('/api/professions'),
        api.get('/api/center/invites'),
      ]);
      setCenter(s.data.center);
      setStaff(s.data.staff || []);
      setProfessions(p.data || []);
      setInvites((i.data.items || []).filter((x) => x.status === 'pending'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento organico');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      email: s.email, password: '', name: s.name || '', profession_code: s.profession_code || '',
      specializations: (s.specializations || []).join(', '), bio: s.bio || '',
      hourly_rate: s.hourly_rate ?? '', phone: s.phone || '', region: s.region || '',
      province: s.province || '', comune: s.comune || '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.profession_code) { toast.error('Nome e professione sono obbligatori'); return; }
    if (!editing && (!form.email || !form.password)) { toast.error('Email e password sono obbligatorie'); return; }
    const payload = {
      name: form.name,
      profession_code: form.profession_code,
      specializations: form.specializations.split(',').map((x) => x.trim()).filter(Boolean),
      bio: form.bio,
      hourly_rate: form.hourly_rate === '' ? 0 : Number(form.hourly_rate),
      phone: form.phone || null,
      region: form.region || null,
      province: form.province || null,
      comune: form.comune || null,
    };
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/api/center/staff/${editing.professional_id}`, payload);
        toast.success('Scheda aggiornata');
      } else {
        await api.post('/api/center/staff', { ...payload, email: form.email, password: form.password });
        toast.success('Professionista aggiunto all\u2019organico');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/api/center/staff/${s.professional_id}/active?active=${!s.internal_active}`);
      toast.success(s.internal_active ? 'Professionista disattivato' : 'Professionista riattivato');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Rimuovere ${s.name} dall\u2019organico?`)) return;
    try {
      const r = await api.delete(`/api/center/staff/${s.professional_id}`);
      toast.success(r.data.outcome === 'detached' ? 'Professionista tornato indipendente' : 'Scheda eliminata');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore rimozione'); }
  };

  const sendInvite = async () => {
    if (!inviteEmail) { toast.error('Inserisci l\u2019email del professionista'); return; }
    try {
      await api.post('/api/center/invites', { email: inviteEmail });
      toast.success('Invito inviato');
      setInviteEmail('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore invito'); }
  };

  const revokeInvite = async (id) => {
    try {
      await api.delete(`/api/center/invites/${id}`);
      toast.success('Invito revocato');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const labelOf = (code) => professions.find((p) => p.code === code)?.label || code;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-12" data-testid="center-staff-page">
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading font-semibold text-4xl mb-2">Organico del Centro</h1>
            <p className="text-foreground/70 max-w-2xl text-sm">
              I professionisti interni hanno una scheda completa ma non compaiono nelle ricerche dei
              clienti: nelle ricerche compare il Centro, per le sole prestazioni coperte dal tuo organico.
            </p>
          </div>
          <Button className="rounded-full" onClick={openCreate} data-testid="add-staff-btn">
            <Plus size={16} className="mr-1" /> Aggiungi professionista
          </Button>
        </div>

        <Card className="p-5 rounded-2xl mb-6 bg-[#F5FAFF] border-[#E3F2FD]" data-testid="center-coverage">
          <div className="flex items-start gap-3">
            <Stethoscope size={22} className="text-primary mt-0.5" weight="duotone" />
            <div className="flex-1">
              <div className="font-semibold text-sm mb-1">
                Prestazioni coperte · {center?.staff_count || 0} professionisti attivi in organico
              </div>
              {(center?.covered_profession_codes || []).length === 0 ? (
                <p className="text-sm text-amber-800">
                  Nessuna professione coperta: finché l'organico è vuoto il Centro non compare nelle
                  ricerche dei clienti.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {center.covered_profession_codes.map((c) => (
                    <Badge key={c} className="bg-primary/10 text-primary" data-testid={`coverage-${c}`}>
                      {labelOf(c)}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card className="p-5 rounded-2xl mb-8" data-testid="center-invites-card">
          <div className="flex items-center gap-2 mb-3">
            <EnvelopeSimple size={18} className="text-primary" weight="duotone" />
            <h2 className="font-semibold text-base">Invita un professionista già iscritto</h2>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            <Input
              type="email"
              placeholder="email del professionista"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="max-w-xs"
              data-testid="invite-email-input"
            />
            <Button variant="outline" className="rounded-full" onClick={sendInvite} data-testid="send-invite-btn">
              Invia invito
            </Button>
          </div>
          <p className="text-xs text-foreground/60 mb-3">
            Se accetta diventa parte del tuo organico e sparisce dalle ricerche dirette dei clienti.
          </p>
          {invites.length > 0 && (
            <div className="space-y-2">
              {invites.map((i) => (
                <div key={i.invite_id} className="flex items-center justify-between gap-3 p-2 rounded-lg bg-muted/40 text-sm" data-testid={`invite-${i.invite_id}`}>
                  <span>{i.professional_name || i.email} · <span className="text-foreground/60">in attesa di risposta</span></span>
                  <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => revokeInvite(i.invite_id)} data-testid={`revoke-invite-${i.invite_id}`}>
                    Revoca
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {loading ? (
          <div className="text-center py-12 text-foreground/60">Caricamento…</div>
        ) : staff.length === 0 ? (
          <Card className="p-8 text-center rounded-2xl bg-muted/20" data-testid="staff-empty">
            <UsersThree size={32} className="mx-auto mb-3 text-foreground/40" weight="duotone" />
            <p className="text-foreground/70">Nessun professionista in organico. Aggiungi il primo per iniziare a ricevere richieste.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="staff-grid">
            {staff.map((s) => (
              <Card key={s.professional_id} className={`p-5 rounded-2xl ${s.internal_active ? '' : 'opacity-60'}`} data-testid={`staff-${s.professional_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="font-semibold text-base">{s.name}</div>
                    <div className="text-xs text-foreground/60">{s.email}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={s.internal_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}>
                      {s.internal_active ? 'Attivo' : 'Disattivato'}
                    </Badge>
                    {s.from_invite && <Badge variant="outline" className="text-[10px]">Su invito</Badge>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <Badge className="bg-primary/10 text-primary">{labelOf(s.profession_code)}</Badge>
                  {(s.specializations || []).slice(0, 3).map((x, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{x}</Badge>
                  ))}
                </div>
                <div className="text-xs text-foreground/70 flex items-center gap-1 mb-2">
                  <MapPin size={12} /> {[s.comune, s.province, s.region].filter(Boolean).join(' · ') || 'Zona non indicata'}
                  {s.hourly_rate ? ` · €${s.hourly_rate}/h` : ''}
                </div>
                <div className="text-xs text-foreground/70 mb-2" data-testid={`staff-availability-${s.professional_id}`}>
                  <span className="text-foreground/50">Disponibilità: </span>
                  {DAY_LABELS.filter(([k]) => (s.availability_slots?.[k] || []).length > 0).length === 0 ? (
                    <span className="text-foreground/50">non indicata dal professionista</span>
                  ) : (
                    DAY_LABELS
                      .filter(([k]) => (s.availability_slots?.[k] || []).length > 0)
                      .map(([k, label]) => `${label} ${(s.availability_slots[k]).map((x) => `${x.start}-${x.end}`).join('/')}`)
                      .join(' · ')
                  )}
                  {(s.absences || []).length > 0 && (
                    <span className="text-amber-700"> · {s.absences.length} assenze programmate</span>
                  )}
                </div>
                {s.bio && <p className="text-sm text-foreground/80 line-clamp-2 mb-3">{s.bio}</p>}
                <div className="flex flex-wrap gap-2 pt-3 border-t border-border/60">
                  <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.professional_id}`}>
                    <PencilSimple size={14} className="mr-1" /> Modifica
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => toggle(s)} data-testid={`toggle-staff-${s.professional_id}`}>
                    <Power size={14} className="mr-1" /> {s.internal_active ? 'Disattiva' : 'Riattiva'}
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-full h-8 text-red-600" onClick={() => remove(s)} data-testid={`remove-staff-${s.professional_id}`}>
                    <Trash size={14} className="mr-1" /> Rimuovi
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="staff-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica scheda professionista' : 'Nuovo professionista interno'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Email di accesso *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" />
                </div>
                <div>
                  <Label className="text-xs">Password *</Label>
                  <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nome e cognome *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" />
              </div>
              <div>
                <Label className="text-xs">Professione *</Label>
                <Select value={form.profession_code} onValueChange={(v) => setForm({ ...form, profession_code: v })}>
                  <SelectTrigger data-testid="staff-profession"><SelectValue placeholder="Scegli" /></SelectTrigger>
                  <SelectContent>
                    {professions.map((p) => (
                      <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Specializzazioni (separate da virgola)</Label>
              <Input value={form.specializations} onChange={(e) => setForm({ ...form, specializations: e.target.value })} data-testid="staff-specializations" />
            </div>
            <div>
              <Label className="text-xs">Bio / descrizione</Label>
              <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="staff-bio" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Tariffa €/ora</Label>
                <Input type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="staff-rate" />
              </div>
              <div>
                <Label className="text-xs">Telefono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-phone" />
              </div>
              <div>
                <Label className="text-xs">Regione</Label>
                <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="staff-region" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Provincia</Label>
                <Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="staff-province" />
              </div>
              <div>
                <Label className="text-xs">Comune</Label>
                <Input value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} data-testid="staff-comune" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)} data-testid="staff-cancel">Annulla</Button>
            <Button className="rounded-full" onClick={save} disabled={saving} data-testid="staff-save">
              {saving ? 'Salvataggio…' : editing ? 'Salva modifiche' : 'Crea professionista'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CenterStaffPage;
